import React, { useState, useEffect } from "react";
import { Search, Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import logo from "./logo.jpg";
import servicesService from "./services/servicesService";
import settingsService from "./services/settingsService";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [servicesList, setServicesList] = useState([]);
  const [businessName, setBusinessName] = useState("VJ Dsign Studio");

  useEffect(() => {
    async function loadHeaderData() {
      try {
        const [services, settings] = await Promise.all([
          servicesService.getActive(),
          settingsService.getSetting("general"),
        ]);

        if (services && services.length > 0) {
          setServicesList(services);
        }
        if (settings?.business_name) {
          setBusinessName(settings.business_name);
        }
      } catch (err) {
        // Fallback safely
      }
    }
    loadHeaderData();
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About Us", href: "#about-us" },
    { name: "Services", href: "#services", hasDropdown: true },
    { name: "Projects", href: "#projects" },
    { name: "Packages", href: "#packages" },
    { name: "Gallery", href: "#gallery" },
    { name: "Reviews", href: "#testimonials" },
    { name: "Contact", href: "#contact" },
  ];

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setIsMobileSearchOpen(false);
    setIsMobileServicesOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100/80 transition-all duration-300">
      <div className="max-w-full lg:max-w-[94%] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20 relative">
          
          {/* Logo + Brand */}
          <a
            href="#home"
            onClick={closeAllMenus}
            className="flex items-center gap-3 cursor-pointer group z-10 no-underline shrink-0"
          >
            <div className="transition-all duration-500 group-hover:scale-105">
              <img
                src={logo}
                alt={businessName}
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-extrabold tracking-[1px] text-neutral-800 transition-all duration-300 group-hover:text-[#9BB7A3]">
                {businessName}
              </h1>
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block -mt-0.5">
                Interior & Architecture
              </span>
            </div>
          </a>

          {/* Desktop Search Box */}
          <div className="hidden xl:block relative max-w-xs w-full mx-4 group">
            <input
              type="text"
              placeholder="Search services & designs..."
              className="w-full py-2 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50/50 text-xs outline-none focus:border-[#9BB7A3] focus:bg-white focus:ring-4 focus:ring-[#9BB7A3]/10 transition-all duration-300"
            />
            <Search
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#9BB7A3]"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 z-10">
            <ul className="flex items-center gap-4 xl:gap-5 font-bold text-xs text-slate-700 uppercase tracking-wider whitespace-nowrap">
              {navLinks.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <li
                      key={item.name}
                      className="relative cursor-pointer py-2 group text-slate-700 hover:text-[#9BB7A3] transition-colors"
                    >
                      <a href={item.href} className="flex items-center gap-1 text-inherit hover:text-inherit">
                        <span>{item.name}</span>
                        <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                      </a>

                      {/* Dropdown Menu */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white border border-slate-100 shadow-2xl rounded-2xl py-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto">
                        {servicesList.length > 0 ? (
                          servicesList.map((service) => (
                            <a
                              key={service.id}
                              href="#services"
                              onClick={closeAllMenus}
                              className="block px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-[#9BB7A3]/10 hover:text-[#7d9b85] transition-colors capitalize"
                            >
                              {service.name}
                            </a>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-xs text-slate-400">Services Catalog</div>
                        )}
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={item.name} className="relative cursor-pointer py-2 transition-all duration-300 hover:text-[#9BB7A3] group">
                    <a href={item.href} className="text-inherit hover:text-inherit block">
                      {item.name}
                    </a>
                    <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-[#9BB7A3] transition-all duration-300 ease-out group-hover:w-full group-hover:left-0" />
                  </li>
                );
              })}
            </ul>

            {/* Premium CTA Button */}
            <a
              href="#contact"
              className="relative flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#9BB7A3] hover:bg-[#86a38e] text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-[#9BB7A3]/20 hover:shadow-lg transition-all duration-300 no-underline shrink-0"
            >
              <span>Get Quote</span>
              <ArrowRight size={14} />
            </a>
          </nav>

          {/* Mobile Menu Controls */}
          <div className="flex items-center gap-1 lg:hidden z-50">
            <button
              onClick={() => {
                setIsMobileSearchOpen(!isMobileSearchOpen);
                setIsMenuOpen(false);
              }}
              className={`p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-all ${
                isMobileSearchOpen ? "bg-slate-100 text-[#9BB7A3]" : ""
              }`}
              aria-label="Toggle Search"
            >
              {isMobileSearchOpen ? <X size={20} /> : <Search size={20} />}
            </button>
            <button
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                setIsMobileSearchOpen(false);
              }}
              className="p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-all"
              aria-label="Toggle Menu"
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Search Overlay */}
      {isMobileSearchOpen && (
        <div className="lg:hidden p-4 bg-white border-b border-slate-200/80 shadow-md">
          <div className="relative w-full flex items-center">
            <input
              type="text"
              placeholder="Search services & designs..."
              className="w-full py-2.5 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50/50 text-xs focus:border-[#9BB7A3] focus:bg-white outline-none"
              autoFocus
            />
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      <div
        className={`lg:hidden fixed top-20 left-0 w-full h-[calc(100vh-80px)] bg-white border-t border-slate-100 shadow-2xl transition-all duration-300 z-30 overflow-y-auto ${
          isMenuOpen
            ? "translate-x-0 opacity-100 pointer-events-auto"
            : "translate-x-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="px-5 py-6 space-y-4 pb-16">
          <ul className="space-y-1">
            {navLinks.map((item) => {
              if (item.hasDropdown) {
                return (
                  <li key={item.name} className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-100">
                    <button
                      onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                      className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-bold text-slate-800"
                    >
                      <span>Services Catalog</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-300 ${
                          isMobileServicesOpen ? "rotate-180 text-[#9BB7A3]" : "text-slate-400"
                        }`}
                      />
                    </button>
                    {isMobileServicesOpen && (
                      <div className="bg-white/80 py-2 border-t border-slate-100 space-y-1">
                        {servicesList.map((service) => (
                          <a
                            key={service.id}
                            href="#services"
                            onClick={closeAllMenus}
                            className="block px-6 py-2.5 text-xs font-semibold text-slate-600 hover:text-[#9BB7A3]"
                          >
                            {service.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.name}>
                  <a
                    href={item.href}
                    onClick={closeAllMenus}
                    className="block px-4 py-3 rounded-xl text-sm font-bold text-slate-800 hover:bg-[#9BB7A3]/10 hover:text-[#7d9b85] transition"
                  >
                    {item.name}
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="pt-4 border-t border-slate-100">
            <a
              href="#contact"
              onClick={closeAllMenus}
              className="block w-full text-center py-3.5 rounded-full bg-[#9BB7A3] text-white font-bold text-sm shadow-md uppercase tracking-wider no-underline"
            >
              Contact Studio
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;